import { cn } from '@invent/wl-ui-kit-next';
import { Button, Input, Textarea } from '@invent/wl-ui-kit-next/ui';
import { Info } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { MockPanel, NotBuilt } from '../chrome/marks';
import { MockScreen, ViewOnlyNote, useScreenGrant } from '../chrome/mock-screen';
import {
  DEMO_CYCLE,
  DEMO_PEOPLE,
  staffName,
  type IEmailTemplate
} from '../fixtures';
import { day } from '../format';
import { DEMO_PATHS } from '../paths';
import { useDemo } from '../state/demo-state';

/**
 * Setup (step 23) — the one of the three things the requirements ask to be data rather than code
 * that is still the demo's: what Veritant writes to advisors (the outreach templates). Who may do
 * what is survey-api's since SR-228 (Team Portal roles, live at `/team/setup/roles`), and what
 * raises a compliance flag (CMP-08's concern criteria) since SR-222, live at
 * `/team/setup/criteria`, where only the in-consideration thresholds panel is still drawn from
 * here (`CriteriaThresholdsPanel`).
 */

const TEMPLATES_MISSING =
  'Only the account notices are mailed; outreach templates, their placeholders and ' +
  'sending have no route.';

/* ------------------------------------------------------------------------------------------------
 * In-consideration thresholds — a mock panel inside the live criteria screen
 * ---------------------------------------------------------------------------------------------- */

/**
 * The thresholds half of what was "Criteria & thresholds": the concern criteria are survey-api's
 * since SR-222 (`/team/setup/criteria`), and the thresholds that put a record in consideration have
 * no route, so they stay the demo's — a badged panel after the live criteria, drawn through
 * `CriteriaThresholdsSlot`, never restyled to pass as live.
 */
export function CriteriaThresholdsPanel() {
  return (
    <MockPanel
      title="In-consideration thresholds"
      source="no-endpoint"
      description={
        'The AUM and production thresholds that put a record in consideration — a boolean ' +
        'formula the survey owner sets before the survey opens (PRS §5 and §6.1). They have no ' +
        'route yet.'
      }
    >
      <p className="text-[0.8125rem]">
        They are set on{' '}
        <Link
          to={DEMO_PATHS.initialReview}
          className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
        >
          Initial review
        </Link>
        , where the records a change lets in or leaves out are seen as it is made.
      </p>
    </MockPanel>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Templates
 * ---------------------------------------------------------------------------------------------- */

const DANA = DEMO_PEOPLE.find((entry) => entry.id === 'p-whitfield');

/** What each placeholder reads for Dana Whitfield, the cast member the preview is written to. */
const PREVIEW_VALUES: Record<string, string> = {
  first_name: DANA?.name.split(' ')[0] ?? 'Dana',
  ranking: `Best-in-State ${DEMO_CYCLE}`,
  cycle: DEMO_CYCLE,
  survey: 'Financial Advisor survey',
  analyst_name: staffName(DANA?.assignedTo ?? null),
  booking_link: 'https://book.example.com/priya-natarajan',
  data_as_of: day('2026-06-30'),
  closes_on: day('2026-10-31'),
  portal_link: 'https://portal.example.com/portal'
};

const PLACEHOLDER = /\{\{\s*([a-z_]+)\s*\}\}/g;

/** The text with each placeholder filled, the filled parts marked, an unknown one left as typed. */
function fill(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(PLACEHOLDER)) {
    const at = match.index ?? 0;
    if (at > last) parts.push(text.slice(last, at));
    const value = PREVIEW_VALUES[match[1]];
    parts.push(
      value !== undefined ? (
        <mark
          key={at}
          className="rounded-sm bg-[color-mix(in_srgb,var(--color-turquoise)_18%,white)] px-0.5 text-inherit"
        >
          {value}
        </mark>
      ) : (
        <span
          key={at}
          title="No value for this placeholder"
          className="rounded-sm border border-dashed border-[var(--color-deep-magenta)] px-0.5 text-[var(--color-deep-magenta)]"
        >
          {match[0]}
        </span>
      )
    );
    last = at + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function TemplatesPage() {
  const { state } = useDemo();
  const templates = state.act5.templates;
  const [selectedId, setSelectedId] = React.useState(templates[0]?.id ?? '');
  const selected = templates.find((template) => template.id === selectedId) ?? templates[0];

  return (
    <MockScreen
      screen="templates"
      title="Templates"
      crumb="Setup"
      lede="The emails Veritant sends advisors during the cycle, with their placeholders."
      missing={TEMPLATES_MISSING}
    >
      <div className="grid gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
        <MockPanel title="Outreach templates" className="h-fit">
          <ul className="grid gap-1" aria-label="Templates">
            {templates.map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  aria-current={template.id === selected?.id ? 'true' : undefined}
                  onClick={() => setSelectedId(template.id)}
                  className={cn(
                    'grid w-full gap-0.5 rounded-md border px-3 py-2 text-left text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    template.id === selected?.id
                      ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-turquoise)_12%,white)]'
                      : 'border-border hover:border-[var(--veritant-royal-55)]'
                  )}
                >
                  <span className="font-medium text-[var(--color-royal-blue)]">{template.name}</span>
                  <span className="text-xs text-muted-foreground">{template.purpose}</span>
                </button>
              </li>
            ))}
          </ul>
        </MockPanel>

        {selected && <TemplateEditor key={selected.id} template={selected} />}
      </div>
    </MockScreen>
  );
}

function TemplateEditor({ template }: { template: IEmailTemplate }) {
  const { dispatch } = useDemo();
  const { acts } = useScreenGrant();
  const [subject, setSubject] = React.useState(template.subject);
  const [body, setBody] = React.useState(template.body);
  const [saved, setSaved] = React.useState(false);
  const changed = subject !== template.subject || body !== template.body;

  return (
    <div className="grid min-w-0 gap-6 2xl:grid-cols-2">
      <MockPanel
        title={template.name}
        source="no-endpoint"
        description={template.purpose}
        className="h-fit"
      >
        <form
          className="grid gap-3 text-[0.8125rem]"
          onSubmit={(event) => {
            event.preventDefault();
            dispatch({ type: 'template/edit', id: template.id, subject, body });
            setSaved(true);
          }}
        >
          <fieldset disabled={!acts} className="grid gap-3">
            <div className="grid gap-1">
              <label htmlFor="template-subject" className="font-medium">
                Subject
              </label>
              <Input
                id="template-subject"
                value={subject}
                onChange={(event) => {
                  setSubject(event.target.value);
                  setSaved(false);
                }}
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor="template-body" className="font-medium">
                Body
              </label>
              <Textarea
                id="template-body"
                rows={12}
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                  setSaved(false);
                }}
                className="font-mono text-xs leading-relaxed"
              />
            </div>
          </fieldset>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            Placeholders: {Object.keys(PREVIEW_VALUES).map((key) => `{{${key}}}`).join(', ')}.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" disabled={!acts || !changed}>
              Save the template
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!acts || !changed}
              onClick={() => {
                setSubject(template.subject);
                setBody(template.body);
              }}
            >
              Discard changes
            </Button>
            <NotBuilt>Send a test</NotBuilt>
          </div>
          <p role="status" className="min-h-5 text-[var(--color-teal)]">
            {saved && !changed ? 'Saved — in this page’s memory only.' : ''}
          </p>
          <ViewOnlyNote what="the templates" />
        </form>
      </MockPanel>

      <MockPanel
        title="Preview"
        description={`Written to ${DANA?.name ?? 'Dana Whitfield'}, from the demo’s cast. Filled placeholders are marked.`}
        className="h-fit"
      >
        <article aria-label="Preview of the email" className="grid gap-3 text-[0.8125rem]">
          <dl className="grid grid-cols-[4rem_minmax(0,1fr)] gap-y-1 border-b border-border pb-3">
            <dt className="text-muted-foreground">To</dt>
            <dd className="break-all">{DANA?.email}</dd>
            <dt className="text-muted-foreground">Subject</dt>
            <dd className="font-medium">{fill(subject)}</dd>
          </dl>
          <div className="leading-relaxed whitespace-pre-line">{fill(body)}</div>
        </article>
      </MockPanel>
    </div>
  );
}
